import isEmpty from 'lodash/isEmpty'
import React, { Fragment, useState } from 'react'
import {
  Button,
  EmptyState,
  TextBlockInlineMuted,
  TextBlockTwoLineMuted,
  Tooltip,
  useDisclosure,
  useErrorModal,
  useSuccessToast
} from '~/design_system'
import { useAccountMeQuery } from '~/api/accounts'
import { BulkActionResponse, useBulkCompleteMutation, useBulkDismissMutation, useBulkPinMutation } from '~/api/myTasks'
import { Msg, useMsg } from '~/common/localizationUtils'
import { trackedButtonClick } from '~/third_party/analytics'
import { formatDateTimeWithLocale } from '~/common/tzUtils'
import { ResultsLayout } from '~/components/v2/ResultsLayout'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { BulkActionBar, useBulkActionBarContext } from '~/components/v2/ResultsLayout/BulkActionBar/BulkActionBar'
import { DefaultListSadStates, ListLayout } from '~/components/v2/ResultsLayout/ListLayout'
import { AutoResultItem, ResultActionMenu, ResultAttributes } from '~/components/v2/ResultsLayout/ResultItem'
import {
  TitleLockupAdditionalInfo,
  TitleLockupHeading,
  TitleLockupStatusBadge,
  TitleLockupSupportText
} from '~/components/v2/TitleLockup'

import { getDataStoreValue, useCategorizedTasks, useStatusBadge } from '~/user_tasks/components/utils'
import { PinnedIconCheckbox } from '~/user_tasks/components/PinnedIconCheckbox'
import { TaskActionButtons } from '~/user_tasks/components/TasksActionButton'
import { TasksConfirmBulkAction } from '~/user_tasks/components/TasksConfirmBulkAction'
import { UserTaskPageTab } from '~/user_tasks/constants'
import { UserTask } from '~/user_tasks/types'
import { actionBarDef, filtersDef, tasksListDataDef } from './filtersConfig'

export const UserTasksList = () => (
  <ListLayout
    filtersDef={filtersDef}
    actionBarDef={actionBarDef}
    listDataDef={tasksListDataDef}
    ContentArea={UserTasksListContent}
    BulkActionBar={UserTasksBulkActionBar}
    SadStates={TaskListSadStates}
  />
)

const UserTasksListContent = () => {
  const { queryResult } = tasksListDataDef.useContext()
  const tasks = queryResult.isLoading ? [] : queryResult.data?.results ?? []
  const categorizedTasks = useCategorizedTasks(tasks)

  return (
    <ResultsLayout.Items data-testid="tasks-extended-desktop-box">
      {Object.entries(categorizedTasks).map(([categoryName, categoryTasks]) => (
        <Fragment key={categoryName}>
          <ResultsLayout.ItemsSectionHeader data-testid={`category-${categoryName.trim()}`}>
            {categoryName}
          </ResultsLayout.ItemsSectionHeader>
          {categoryTasks.map((item: UserTask) => (
            <TaskItem key={item.id} task={item} />
          ))}
        </Fragment>
      ))}
    </ResultsLayout.Items>
  )
}

interface CondenseAssignedRolesParams {
  roleTags: string[]
  limit: number
}

export interface TaskItemProps {
  task: UserTask
}

export const TaskItem = ({ task }: TaskItemProps) => {
  const msg = useMsg()
  const { data: me } = useAccountMeQuery()
  const taskArrayFromObject = Object.entries(task.data_store || {}).map(([key, value]) => ({ [key]: value }))
  const assignDetails = getAssignDetails(task.role_tags, msg)
  const getStatusBadgeData = useStatusBadge()

  return (
    <AutoResultItem data-testid={`tasks-details-stack-${task.id}`}>
      <BulkActionBar.Checkbox data-testid={`user-tasks-checkbox-${task.id}`} item={task} />
      <TitleLockupHeading href={task.source} data-testid={`user-task-description-link-${task.id}`}>
        {task.description}
      </TitleLockupHeading>
      <PinnedIconCheckbox data-slot="titleLockupIcon" task={task} />
      <TitleLockupStatusBadge variant={getStatusBadgeData(task.status).code}>
        {getStatusBadgeData(task.status).title}
      </TitleLockupStatusBadge>
      <TitleLockupSupportText>{task.subtype}</TitleLockupSupportText>
      <TitleLockupAdditionalInfo>
        <TextBlockInlineMuted
          primaryText={msg('common.ui.due_date')}
          secondaryText={formatDateTimeWithLocale(task.due_date, me?.timezone, me?.locale, undefined, true)}
        />
        {assignDetails && (
          <Tooltip
            label={
              assignDetails.tooltip
                ? `${msg('tasks.actions.assign_details')} (${assignDetails.tooltip})`
                : msg('tasks.actions.assign_details')
            }
            placement="bottom"
            hasArrow>
            <TextBlockInlineMuted
              primaryText={msg('tasks_extended_layout.assigned_to_roles')}
              secondaryText={assignDetails.text}
              alignItems="flex-start"
            />
          </Tooltip>
        )}
      </TitleLockupAdditionalInfo>
      <ResultAttributes>
        {taskArrayFromObject?.map(item => (
          <TextBlockTwoLineMuted
            key={Object.keys(item)[0]}
            primaryText={Object.keys(item)[0]}
            secondaryText={getDataStoreValue(task.data_store, Object.keys(item)[0])}
          />
        ))}
      </ResultAttributes>
      <ResultActionMenu data-testid={`tasks-actions-menu-btn-${task.id}`}>
        <TaskActionButtons taskID={task.id} subType={task.subtype} isPinned={task.is_pinned} />
      </ResultActionMenu>
    </AutoResultItem>
  )
}

function condenseAssignedRoles({ roleTags, limit }: CondenseAssignedRolesParams) {
  const slicedUp = roleTags.slice(0, limit).join(', ')
  return roleTags.length > limit
    ? `${slicedUp} and ${roleTags.length - limit} other${roleTags.length - limit > limit ? 's' : ''}`
    : slicedUp
}

function getAssignDetails(roleTags: any, msg: any, limit = 2) {
  if (isEmpty(roleTags)) return null

  return {
    text: condenseAssignedRoles({ roleTags, limit }),
    tooltip: roleTags.length > limit && roleTags.join(', ')
  }
}

type BulkAction = 'bulkPin' | 'bulkDismiss' | 'bulkComplete' | null

export const UserTasksBulkActionBar = () => {
  const bulkActionBar = useBulkActionBarContext()
  const { queryParams } = tasksListDataDef.useContext()
  const selectedItemIds = bulkActionBar.areAllItemsOnAllPagesSelected
    ? 'All'
    : bulkActionBar.selectedItems.map(item => Number(item.id))

  const msg = useMsg()
  const { onClose } = useDisclosure()
  const successToast = useSuccessToast()
  const { showModal: showErrorModal } = useErrorModal()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [activeBulkAction, setActiveBulkAction] = useState<BulkAction>(null)

  const { mutateAsync: bulkPinTasks, isLoading: isLoadingPin } = useBulkPinMutation()
  const { mutateAsync: bulkDismissTasks, isLoading: isLoadingDismiss } = useBulkDismissMutation()
  const { mutateAsync: bulkCompleteTasks, isLoading: isLoadingComplete } = useBulkCompleteMutation()

  const resetConfirmModal = () => {
    setShowConfirmModal(false)
    setActiveBulkAction(null)
  }

  type ActionConfig = {
    mutateFn: typeof bulkPinTasks | typeof bulkDismissTasks | typeof bulkCompleteTasks
    title: string
    descriptionSuffix: string
    partialSuccessToastMsg: (respData: BulkActionResponse) => string
    fullSuccessToastMsg: string
    errorToastMsg: string
  }
  const actionConfig: Record<Exclude<BulkAction, null>, ActionConfig> = {
    bulkPin: {
      mutateFn: bulkPinTasks,
      title: msg('user_tasks.pin_task'),
      descriptionSuffix: `${msg('user_tasks.pin_number_selected_items_tasks', {
        numSelectedItems: bulkActionBar.selectedItemsCount
      })}`,
      partialSuccessToastMsg: (respData: BulkActionResponse) =>
        msg('user_tasks.number_of_tasks_pinned_successfully', { successCount: respData.success_count }),
      fullSuccessToastMsg: msg('user_tasks.tasks_pinned_successfully'),
      errorToastMsg: msg('user_tasks.tasks_failed_to_pin')
    },
    bulkDismiss: {
      mutateFn: bulkDismissTasks,
      title: msg('user_tasks.dismiss_tasks_for_me'),
      descriptionSuffix: `${msg('user_tasks.dismiss_number_selected_items_tasks_for_yourself', {
        numSelectedItems: bulkActionBar.selectedItemsCount
      })}`,
      partialSuccessToastMsg: (respData: BulkActionResponse) =>
        msg('user_tasks.number_selected_tasks_dismissed_successfully', { successCount: respData.success_count }),
      fullSuccessToastMsg: msg('user_tasks.tasks_dismissed_successfully'),
      errorToastMsg: msg('user_tasks.tasks_failed_to_dismiss', { count: bulkActionBar.selectedItemsCount })
    },
    bulkComplete: {
      mutateFn: bulkCompleteTasks,
      title: msg('user_tasks.dismiss_tasks_for_all'),
      descriptionSuffix: `${msg('user_tasks.dismiss_number_selected_items_tasks_for_everyone', {
        numSelectedItems: bulkActionBar.selectedItemsCount
      })}`,
      partialSuccessToastMsg: (respData: BulkActionResponse) =>
        msg('user_tasks.number_selected_items_completed_successfully', { successCount: respData.success_count }),
      fullSuccessToastMsg: msg('user_tasks.tasks_completed_successfully'),
      errorToastMsg: msg('user_tasks.tasks_failed_to_mark_complete', { count: bulkActionBar.selectedItemsCount })
    }
  }

  const processBulkAction = async () => {
    if (!activeBulkAction) return
    const action = actionConfig[activeBulkAction]
    try {
      const respData = await action.mutateFn({
        user_task_ids: selectedItemIds,
        query_params: queryParams
      })
      if (!respData.success) {
        successToast(action.partialSuccessToastMsg(respData))
      } else {
        successToast(action.fullSuccessToastMsg)
      }
      onClose()
    } catch {
      showErrorModal({ message: action.errorToastMsg })
    }
    resetConfirmModal()
    bulkActionBar.deselectAllItems()
  }

  return (
    <>
      <BulkActionBar>
        <BulkActionBar.ActionButton
          data-testid="pin-tasks-bulk-action-btn"
          onClick={trackedButtonClick(
            () => {
              setActiveBulkAction('bulkPin')
              setShowConfirmModal(true)
            },
            'pin_tasks',
            {
              selected_count: bulkActionBar.selectedItemsCount,
              all_items_selected: bulkActionBar.areAllItemsOnAllPagesSelected ? 'true' : 'false'
            }
          )}>
          <Msg id="user_tasks.pin_task" description="Pin Tasks" />
        </BulkActionBar.ActionButton>
        <BulkActionBar.ActionButton
          data-testid="dismiss-for-me-bulk-action-btn"
          onClick={trackedButtonClick(
            () => {
              setActiveBulkAction('bulkDismiss')
              setShowConfirmModal(true)
            },
            'dismiss_for_me',
            {
              selected_count: bulkActionBar.selectedItemsCount,
              all_items_selected: bulkActionBar.areAllItemsOnAllPagesSelected ? 'true' : 'false'
            }
          )}>
          <Msg id="user_tasks.dismiss_for_me" description="Dismiss for Me" />
        </BulkActionBar.ActionButton>
        <BulkActionBar.ActionButton
          data-testid="dismiss-for-all-bulk-action-btn"
          onClick={trackedButtonClick(
            () => {
              setActiveBulkAction('bulkComplete')
              setShowConfirmModal(true)
            },
            'dismiss_for_all',
            {
              selected_count: bulkActionBar.selectedItemsCount,
              all_items_selected: bulkActionBar.areAllItemsOnAllPagesSelected ? 'true' : 'false'
            }
          )}>
          <Msg id="user_tasks.dismiss_for_all" description="Dismiss for All" />
        </BulkActionBar.ActionButton>
      </BulkActionBar>
      <TasksConfirmBulkAction
        title={activeBulkAction ? actionConfig[activeBulkAction]?.title : ''}
        descriptionSuffix={activeBulkAction ? actionConfig[activeBulkAction]?.descriptionSuffix : ''}
        isOpen={showConfirmModal}
        onClose={() => resetConfirmModal()}
        onProceed={() => processBulkAction()}
        isLoading={isLoadingPin || isLoadingDismiss || isLoadingComplete}
      />
    </>
  )
}

function TaskListSadStates({ children }: { children: React.ReactNode }) {
  const { queryResult, queryParams } = tasksListDataDef.useContext()
  const { view } = useActionBarContext()
  const msg = useMsg()

  if (queryParams.view === UserTaskPageTab.MY_TASKS && queryResult.data?.results?.length === 0 && !queryParams.search) {
    return (
      <EmptyState>
        <EmptyState.CaughtUp title={msg('tasks.no_result_state.youre_all_caught_up')} />
        <Button
          variant="inlineLink"
          onClick={trackedButtonClick(() => view?.onChange?.(UserTaskPageTab.ALL), 'view_all_task')}>
          {msg('tasks.no_result_state.view_all_tasks')}
        </Button>
      </EmptyState>
    )
  }

  if (queryParams.view === UserTaskPageTab.ALL && queryResult.data?.results?.length === 0 && !queryParams.search) {
    return (
      <EmptyState>
        <EmptyState.NothingToShow title={msg('common.ui.empty_state_no_search_results')} />
      </EmptyState>
    )
  }

  return <DefaultListSadStates>{children}</DefaultListSadStates>
}
